import { useEffect, useState } from 'react'

import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import classnames from 'classnames/bind'
import { AnimatePresence, motion } from 'framer-motion'
import { signIn } from 'next-auth/react'
import { FormProvider, useForm } from 'react-hook-form'

import { isofetch } from 'Services/Fetch'
import { useUser } from 'Services/User'

import { diffYearsFromNow } from 'Utils/date'

import styles from 'components/Signup/Signup.module.css'

import Heading from 'components/Heading'
import Loading from 'components/Loading'
import { ChooseSocialAchievemnts, SignupFormModel, SignupInitialValues, SignupValidations } from 'components/Signup'
import SignupImage from 'components/Signup/SignupImage'
import SignupVideo from 'components/Signup/SignupVideo'

import HeroBg from '/public/hero-bg.jpg'
import IntroVideo from '/public/intro-video-thumbnail.jpg'

const cx = classnames.bind(styles)

const ChooseSignup = dynamic(() => import('components/Signup').then((mod) => mod.ChooseSignup))
const ChooseProfile = dynamic(() => import('components/Signup').then((mod) => mod.ChooseProfile))
const ChooseRegion = dynamic(() => import('components/Signup').then((mod) => mod.ChooseRegion))
const ChoosePhoto = dynamic(() => import('components/Signup').then((mod) => mod.ChoosePhoto))
const ChooseCoverBg = dynamic(() => import('components/Signup').then((mod) => mod.ChooseCoverBg))
const ChooseAbout = dynamic(() => import('components/Signup').then((mod) => mod.ChooseAbout))

const { formField } = SignupFormModel

const steps = {
  account: {
    image: HeroBg,
    video: 'https://vimeo.com/574993863/24fb98794e',
    slug: 'account',
    component: ChooseSignup,
  },
  profile: {
    image: IntroVideo,
    video: 'https://vimeo.com/574994096/dfc2772b6d',
    slug: 'profile',
    component: ChooseProfile,
  },
  region: {
    image: HeroBg,
    video: 'https://vimeo.com/574994237/3fd9138301',
    slug: 'region',
    component: ChooseRegion,
  },
  photo: {
    image: IntroVideo,
    video: 'https://vimeo.com/574994412/b5857db337',
    slug: 'photo',
    component: ChoosePhoto,
  },
  cover: {
    image: HeroBg,
    video: 'https://vimeo.com/574994583/73f52d4567',
    slug: 'cover',
    component: ChooseCoverBg,
  },
  about: {
    image: HeroBg,
    video: 'https://vimeo.com/574993863/24fb98794e',
    slug: 'about',
    component: ChooseAbout,
  },
  social: {
    image: HeroBg,
    video: 'https://vimeo.com/574993863/24fb98794e',
    slug: 'social',
    component: ChooseSocialAchievemnts,
  },
  building: {
    image: HeroBg,
    component: () => <Loading center title={`Building Your Profile Now...`} />,
  },
}

export default function Signup() {
  const { loggedIn, status, profile } = useUser()
  const [activeStep, setActiveStep] = useState()
  const [building, setBuilding] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formErrorMessage, setFormErrorMessage] = useState()
  const router = useRouter()

  const methods = useForm({
    defaultValues: SignupInitialValues,
  })

  const profile_type = methods.watch('profile_type')
  const date_of_birth = methods.watch('date_of_birth')

  if (loggedIn) {
    router.push(`/dashboard`)
  }

  useEffect(() => {
    if (router.query.step) {
      setActiveStep(router.query.step)
    } else {
      router?.push('/signup?step=account', undefined, { shallow: true })
    }
  }, [router.query.step])

  async function submitForm(formData) {
    // console.log({ formData })

    setSubmitting(false)
    const values = {
      ...formData,
      email: formData.email.toLowerCase(),
    }

    const personal_achievements = formData.personal_achievements.filter((item) => item.link_value !== '')
    values.personal_achievements = personal_achievements

    try {
      const response = await isofetch(`/api/account/signup`, 'POST', values)

      if (response.success) {
        await signIn('credentials', {
          email: values.email.toLowerCase(),
          password: values.password,
        })
      } else {
        throw new Error(response.error)
      }
    } catch (error) {
      setFormErrorMessage(error)
      setSubmitting(false)
      router?.push('/signup?step=account', undefined, { shallow: true })
    }
  }

  async function onSubmit(values) {
    setSubmitting(true)
    setFormErrorMessage()
    // console.log(values)

    const stepKeys = Object.keys(steps)
    const totalSteps = stepKeys.length
    const currentStep = values.step
    const currentStepIndex = stepKeys.findIndex((i) => i === currentStep)
    let nextStepSlug = stepKeys[currentStepIndex + 1]

    const diff_years = diffYearsFromNow(new Date(date_of_birth))
    if (diff_years < 18) {
      methods.setValue('profile_type', 'student')
      nextStepSlug = stepKeys[currentStepIndex + 2]
    }
    // console.log({ values, currentStep, nextStepSlug })

    if (currentStepIndex >= 0 && currentStep !== 'social') {
      router.push(`/signup?step=${nextStepSlug}`, undefined, { shallow: true })

      setSubmitting(false)
    } else {
      setBuilding(true)
      submitForm(values)
    }
  }

  const handleSubmit = () => {
    methods.handleSubmit(onSubmit)()
  }
  const CurrentStep = steps[activeStep]?.component
  const step_number = Number(Object.keys(steps).findIndex((key) => key === activeStep)) + 1
  const total_steps = Number(Object.keys(steps).length) - 1

  const classes = cx({
    'signup-page-steps': true,
    'signup-step-full': steps[activeStep]?.slug === 'social',
  })

  return (
    <div className={styles['signup-page']} style={{ '--app-height': `calc(100vh - 40px)` }}>
      {!building && (
        <div className={styles['signup-progress']}>
          <Heading element="h4" nomargin>{`Step ${step_number} of ${total_steps}`}</Heading>
        </div>
      )}
      <div className={styles['signup-page-wrapper']}>
        {building && (
          <div>
            <Loading center title={`Building Your Profile Now...`} />
          </div>
        )}

        {!building && !loggedIn && status !== 'loading' && activeStep && (
          <>
            <div className={classes}>
              <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} className={styles['signup-form']}>
                  <AnimatePresence initial={false}>
                    <motion.div
                      key={activeStep}
                      initial={{ x: '100%', opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: '-100%', opacity: 0 }}
                      transition={{ duraton: 4 }}
                      className={styles['signup-form-wrapper']}
                    >
                      <CurrentStep
                        {...methods}
                        setActiveStep={setActiveStep}
                        handleSubmit={handleSubmit}
                        formField={formField}
                        isSubmitting={submitting || methods?.formState?.isValidating}
                        user={profile}
                        profile_type={profile_type}
                        formErrorMessage={formErrorMessage}
                      />
                    </motion.div>
                  </AnimatePresence>
                </form>
              </FormProvider>
            </div>
            {activeStep && steps[activeStep]?.slug !== 'social' && (
              <div className={styles['signup-page-images']}>
                {activeStep && steps[activeStep].video && <SignupVideo step={steps[activeStep]} />}

                {activeStep && !steps[activeStep].video && <SignupImage step={steps[activeStep]} />}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
